import React, {CSSProperties, FC, PropsWithChildren} from 'react';
import './StateLabel.less';
import newHRTimestamp, {hrtToSeconds} from "../../lib/picos/timestamp";
import {Tooltip} from "react-tooltip";

type StyleInjectorProps = PropsWithChildren<{
    clsName?: string,
    style?: CSSProperties,
    title?: string | number,
    tooltipId?: string
}>;

const StyleInjector = (props: StyleInjectorProps) => {
    return React.Children.map(props.children, (element: any) => {
        if (!element) return null;

        const className = `${element?.props?.clsName ?? ''} ${props.clsName ?? ''}`.trim();

        // If this is just text (a text node), we need to wrap it in a span
        if (typeof (element) === 'string') {
            return <span className={className}
                         data-tooltip-id={props.tooltipId}
            >
                {element}
            </span>;
        }

        // If this is a regular node, we need to augment its className
        else {
            // return React.cloneElement(element, {className, ref, "data-tooltip-id": props.tooltipId});
            return React.cloneElement(element, {className});
        }
    });
};

export interface BaseStateLabelProps {
    // Some objects are not saved until an initial change is made, like with Options.
    // For those, we only show non-persisted if it has been saved before
    ignoreUntilSaved?: boolean,

    // Indicates that a change has been made, and not persisted, and that it will not be persisted until the parent is saved.
    waitingForParent?: boolean,

    // Custom styles
    style?: CSSProperties,

    // And possibly a title
    title?: string
}

export interface StateLabelPersisted extends BaseStateLabelProps {
    persisted?: boolean,
    fatalError?: {
        message: string,
        status: string | number
    }
}

export interface StateLabelItem extends StateLabelPersisted {
    item: {
        id: string,
        addedOn?: number,
        dbAddedOn?: number | null,
        persisted?: boolean,
        fatalError?: {
            message?: string,
            status: string | number
        } | null
    }
}

export type StateLabelProps = PropsWithChildren<StateLabelPersisted & StateLabelItem>;

/**
 * StateLabel is used whenever we want some text that displays a color-coded state based on saved state.
 *
 * Specifically:
 * - black if the referenced item's state has been saved to the database.
 * - blue if the referenced item's state has not been saved to the database.
 * - maroon if the referenced item's state has made an attempt to save, but has failed.
 *          Hovering over a maroon item should show the error message.
 *
 * We allow two methods for using a StateLabel:
 * 1. Pass in an object implementing the fields in Base.js
 * 2. Pass in persisted and/or fatalError properties.
 */
const StateLabel: FC<StateLabelProps> = (props: StateLabelProps) => {
    // We need to keep track of what has been injected, so that we can remove it!
    const persisted = props.item ? props.item.persisted : props.persisted;
    const fatalError = props.item ? props.item.fatalError : props.fatalError;
    const title = fatalError ? fatalError.message || fatalError.status || props.title : props.title;

    let className = '';

    const notPersistedClass = props.waitingForParent ? 'state-label__waiting-for-parent' : 'state-label__not-persisted';

    // Determine class flags

    // First check for errors
    if (fatalError && (fatalError.message || fatalError.status)) {
        className = 'state-label__error';
    }

    // If no fatal errors, and it's persisted, just return the children
    else if (persisted) return props.children;

    // Then check if the item has been persisted. If so, just return the children as-is.
    else {
        // We are now left with non-persisted items. Check if it is a special case
        if (props.ignoreUntilSaved) {
            // Special case. Only show blue if this is a VERY recent change (addedOn)
            if (props.item?.dbAddedOn) {
                className = notPersistedClass;
            }

            // Only show blue if addedOn is VERY recent (meaning it just got changed)
            else if (props.item?.addedOn) {
                const interval = hrtToSeconds(newHRTimestamp() - props.item.addedOn);
                if (interval < 5) className = notPersistedClass;
                else className = '';
            }

            else className = '';
        }

        // Plain old non-persisted item
        else {
            className = notPersistedClass;
        }
    }

    let existingClasses: string[] = [];
    let isArray: boolean;

    if (Array.isArray(props.children)) {
        isArray = true;
        existingClasses = props.children?.map(child => child.props?.className);
    }
    else {
        isArray = false;
        React.Children.forEach(props.children, (element: any) => {
            if (element.props?.className) {
                existingClasses.push(element.props.className);
            }
        });
    }

    const tooltipId = `state-label-tooltip-${props.item?.id || props.item?.addedOn || 0}`;

    if (className.trim()) {
        if (fatalError) return <>
            <Tooltip id={tooltipId}/>
            <StyleInjector clsName={`${existingClasses} ${className}`}
                           style={props.style}
                           tooltipId={tooltipId}
            >
                {props.children}
            </StyleInjector>
        </>;
        else {
            if (isArray) {
                console.warn('StateLabel: Injecting style into multiple children. This is not recommended.');

                return React.Children.map(props.children, (c, i) => {
                    return <StyleInjector key={`state-label-${i}`}
                                          clsName={`${existingClasses} ${className}`} style={props.style}
                                          title={title}
                                          tooltipId={tooltipId}
                    >
                        {props.children}
                    </StyleInjector>;
                });
            }
            else {
                return <StyleInjector clsName={`${existingClasses} ${className}`}
                                      style={props.style}
                                      title={title}
                                      tooltipId={tooltipId}
                >
                    {props.children}
                </StyleInjector>;
            }
        }
    }
    else return props.children;
}

export default StateLabel;
